import { FC } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography } from '@altermaind/web/ui';
import { useDrawer } from '@/_shared/components/Drawer';
import { Column, usePaginatedTable } from '@/_shared/components/Table';
import { PaginatedTable } from '@/_shared/components/Table/PaginatedTable';
import { StyledTable, StyledTableDrawer } from '@/_shared/components/Table/StyledTable';
import { ValueInTable } from '@/_shared/components/ValueInTable';
import { CreditsItems, postEmployerCreditsCall } from '@/credits/state';

type Props = {
  items: CreditsItems;
};

export const LastThreeMonths: FC<Props> = ({ items }) => {
  const t = useTranslations('people.details.credits.lastThreeMonths.table');
  const tShared = useTranslations();
  const { isOpen, close, ...drawerProps } = useDrawer();

  const paginedProps = usePaginatedTable({
    rowData: items || [],
    pageSize: 3,
    totalItems: items?.length || 0,
  });

  const drawers: StyledTableDrawer['instances'] = [];

  const columns: Column[] = [
    {
      key: 'month',
      title: t('month'),
      renderValue: (item: { period: string }) => (
        <ValueInTable>
          {`${tShared(`months.${dayjs(item.period).format('MMMM').toLowerCase()}`)} ${dayjs(item.period).format('YYYY')}`}
        </ValueInTable>
      ),
    },
    {
      key: 'residualCredits',
      title: t('residualCredits'),
      renderValue: (item: { residualCredits: number }) => (
        <ValueInTable>{item.residualCredits || 0}</ValueInTable>
      ),
    },
    {
      key: 'receivedCredits',
      title: t('receivedCredits'),
      renderValue: (item: { receivedCredits: number }) => (
        <ValueInTable color='success'>{`+${item.receivedCredits}` || 0}</ValueInTable>
      ),
    },
    {
      key: 'accumulatedCredits',
      title: t('accumulatedCredits'),
      renderValue: (item: { accumulatedCredits: number }) => (
        <ValueInTable>
          {item.accumulatedCredits === 0 ? '0' : `+${item.accumulatedCredits}`}
        </ValueInTable>
      ),
    },
    {
      key: 'spentCredits',
      title: t('spentCredits'),
      renderValue: (item: { spentCredits: number }) => (
        <ValueInTable color='error'>
          {item.spentCredits === 0 ? '0' : `-${item.spentCredits}`}
        </ValueInTable>
      ),
    },
    {
      key: 'balance',
      title: t('balance'),
      titleTooltip: t('balanceTooltip'),
      renderValue: (item: { balance: number }) => <ValueInTable>{item.balance || 0}</ValueInTable>,
    },
  ];

  return (
    <Card overflow='visible' mt='s' display='flex' flexDirection='column'>
      <Box display='flex' alignItems='center'>
        <Typography variant='h3' flex='1'>
          {t('title')}
        </Typography>
      </Box>
      <StyledTable hasElement drawer={{ ...drawerProps, isOpen, close, instances: drawers }}>
        <PaginatedTable
          header-data-testid='table-team-header'
          row-data-testid='table-employer-row'
          columns={columns}
          pageSize={3}
          call={{ ...postEmployerCreditsCall }}
          onRowClick={() => {
            /* empty */
          }}
          rowWrapperProps={(employee: string) => {
            return employee === ''
              ? {
                  backgroundColor: 'grey/dark/background',
                  borderRadius: '10px',
                  position: 'relative',
                }
              : {};
          }}
          paginedProps={paginedProps}
          skeletonProps={{
            hasHeaderRow1: false,
            hasHeaderRow2: true,
            rows: 1,
          }}
          hasHover
          hidePagination
        />
      </StyledTable>
    </Card>
  );
};
